import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, alpha } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SavingsIcon from '@mui/icons-material/Savings';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import DashboardCard from '../components/StatisticsDashboard/DashboardCard';
import MonthRangeFilter from '../components/StatisticsDashboard/MonthRangeFilter';
import TrendAreaChart from '../components/StatisticsDashboard/TrendAreaChart';
import StatusBreakdownChart from '../components/StatisticsDashboard/StatusBreakdownChart';
import CategoryPieChart from '../components/StatisticsDashboard/CategoryPieChart';
import CategoryProgressList from '../components/StatisticsDashboard/CategoryProgressList';
import SavingsChart from '../components/StatisticsDashboard/SavingsChart';
import { getDefaultMonthRange, monthInputToRange, CATEGORICAL_COLORS, STATUS_COLORS } from '../components/StatisticsDashboard/chartUtils';

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || '').trim();

const DASHBOARD_THEME = {
    trend: { color: CATEGORICAL_COLORS[0], icon: TrendingUpIcon },
    status: { color: STATUS_COLORS.good, icon: FactCheckIcon },
    approvers: { color: CATEGORICAL_COLORS[1], icon: PeopleIcon },
    customers: { color: CATEGORICAL_COLORS[6], icon: StorefrontIcon },
    savings: { color: CATEGORICAL_COLORS[2], icon: SavingsIcon },
};

const STATUS_GROUPS = [
    { key: 'success', label: 'הצליחו', color: STATUS_COLORS.good, icon: CheckCircleIcon },
    { key: 'failed', label: 'נכשלו', color: STATUS_COLORS.critical, icon: CancelIcon },
];

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const ApproveLinesStatisticsPage = () => {
    const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
    const [toMonth, setToMonth] = useState(DEFAULT_TO);

    const [monthlyStats, setMonthlyStats] = useState([]);
    const [approverStats, setApproverStats] = useState([]);
    const [customerStats, setCustomerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [minutesSavedPerLine, setMinutesSavedPerLine] = useState(5);
    const [minutesInput, setMinutesInput] = useState('5');
    const [savingMinutes, setSavingMinutes] = useState(false);

    const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
    const { execute: fetchWrite } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.write });

    const loadSettings = useCallback(async () => {
        try {
            const res = await fetchRead('GET', `${BASE_URL}/api/approveLines/stats/settings`);
            const value = res.settings?.minutesSavedPerLine ?? 5;
            setMinutesSavedPerLine(value);
            setMinutesInput(String(value));
        } catch (err) {
            console.error('Error loading approve-lines stats settings:', err);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStats = useCallback(async () => {
        if (!fromMonth || !toMonth) return;
        setLoading(true);
        setError(null);
        try {
            const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
            const params = new URLSearchParams({ startDate, endDate }).toString();
            const [monthlyRes, approverRes, customerRes] = await Promise.all([
                fetchRead('GET', `${BASE_URL}/api/approveLines/stats/monthly-stats?${params}`),
                fetchRead('GET', `${BASE_URL}/api/approveLines/stats/approver-breakdown?${params}`),
                fetchRead('GET', `${BASE_URL}/api/approveLines/stats/customer-breakdown?${params}`),
            ]);
            setMonthlyStats(monthlyRes.monthlyStats || []);
            setApproverStats(approverRes.approverStats || []);
            setCustomerStats(customerRes.customerStats || []);
        } catch (err) {
            console.error('Error loading approve-lines statistics:', err);
            setError(err.message || 'שגיאה בטעינת הסטטיסטיקות');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth]);

    useEffect(() => {
        loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromMonth, toMonth]);

    const handleSaveMinutes = async () => {
        const value = Number(minutesInput);
        if (Number.isNaN(value) || value < 0) return;
        setSavingMinutes(true);
        try {
            const res = await fetchWrite('PUT', `${BASE_URL}/api/approveLines/stats/settings`, { minutesSavedPerLine: value });
            setMinutesSavedPerLine(res.settings?.minutesSavedPerLine ?? value);
        } catch (err) {
            console.error('Error saving approve-lines stats settings:', err);
        } finally {
            setSavingMinutes(false);
        }
    };

    const pageColor = CATEGORICAL_COLORS[0];

    return (
        <Box display="flex" flexDirection="column" gap={3} p={2}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(pageColor, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <BarChartIcon sx={{ color: pageColor, fontSize: 26 }} />
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight={700}>סטטיסטיקות שחרור הזמנות בהשהיית סחר</Typography>
                    <Typography variant="body2" color="text.secondary">
                        תמונת מצב לפי טווח התאריכים שנבחר — משתנה בכל הדשבורדים למטה
                    </Typography>
                </Box>
            </Box>

            <MonthRangeFilter
                fromMonth={fromMonth}
                toMonth={toMonth}
                onFromMonthChange={setFromMonth}
                onToMonthChange={setToMonth}
                color={pageColor}
            />

            {error && <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} p={6}>
                    <CircularProgress sx={{ color: pageColor }} />
                    <Typography variant="body2" color="text.secondary">טוען נתונים...</Typography>
                </Box>
            ) : (
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="מגמת אישורי שורות חודשית"
                            subtitle="כמות שורות שאושרו"
                            icon={DASHBOARD_THEME.trend.icon}
                            color={DASHBOARD_THEME.trend.color}
                        >
                            {(expanded) => (
                                <TrendAreaChart
                                    monthlyData={monthlyStats}
                                    expanded={expanded}
                                    color={DASHBOARD_THEME.trend.color}
                                    totalLabel="סה״כ שורות שאושרו בטווח שנבחר"
                                    unitLabel="שורות"
                                    gradientId="approveLinesTrendGradient"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="חיסכון בזמן עבודה"
                            subtitle="לפי כמות שורות שאושרו בחודש"
                            icon={DASHBOARD_THEME.savings.icon}
                            color={DASHBOARD_THEME.savings.color}
                        >
                            {(expanded) => (
                                <SavingsChart
                                    monthlyData={monthlyStats}
                                    color={DASHBOARD_THEME.savings.color}
                                    minutesSavedPerUnit={minutesSavedPerLine}
                                    minutesInput={minutesInput}
                                    onMinutesInputChange={setMinutesInput}
                                    onSaveMinutes={handleSaveMinutes}
                                    saving={savingMinutes}
                                    expanded={expanded}
                                    minutesFieldLabel="דקות שנחסכות לשורה"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="פילוח לפי סטטוס אישור"
                            subtitle="מגמה חודשית — הצליחו / נכשלו"
                            icon={DASHBOARD_THEME.status.icon}
                            color={DASHBOARD_THEME.status.color}
                        >
                            {(expanded) => (
                                <StatusBreakdownChart
                                    monthlyData={monthlyStats}
                                    expanded={expanded}
                                    statusGroups={STATUS_GROUPS}
                                    totalLabel="סה״כ שורות"
                                />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="מאשרים מובילים"
                            subtitle="כמות שורות שאושרו לכל מאשר בטווח שנבחר"
                            icon={DASHBOARD_THEME.approvers.icon}
                            color={DASHBOARD_THEME.approvers.color}
                        >
                            {(expanded) => (
                                <CategoryPieChart data={approverStats} expanded={expanded} unitLabel="שורות" />
                            )}
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="לקוחות מובילים"
                            subtitle="7 הלקוחות המובילים לפי כמות שורות שאושרו"
                            icon={DASHBOARD_THEME.customers.icon}
                            color={DASHBOARD_THEME.customers.color}
                        >
                            {() => (
                                <CategoryProgressList
                                    data={customerStats}
                                    unitLabel="שורות"
                                    secondaryMetricLabel="הצליחו"
                                    topN={7}
                                />
                            )}
                        </DashboardCard>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default ApproveLinesStatisticsPage;
